import { AutoCompleteSection, CustomTextField, CustomTextFieldAutoCompl } from '../../../../shared/components/CustomTextField';
import { Control, Controller, UseFormSetValue, useForm } from 'react-hook-form';
import { FILTERABLE_FIELDS, useFiltering } from '../../state/filtering.state';
import { FilterValues, LockedStates, isArrayField } from './filter.types';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '@ingka/modal';
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { StyledSheetDiv, StyledSheets, StyledToolbarButton, StyledTooltip } from '../../../../shared/styles/components/integrations.styles';
import { formatItemNumbers, validateItemNumberInput } from '../../utils/item-number.utils';
import { useEnterKeySubmit, useEscapeKey } from '../../../../shared/utils/keyboard.utils';
import { prefixFirstFilter } from '../../../../shared/utils/autocomplete.utils';

import Button from '@ingka/button';
import { StyledIndeterminateCheckBox } from '../../../../shared/components/IndeterminateCheckbox';
import filterIcon from '@ingka/ssr-icon/paths/filters';
import { filterValidationSchema } from './filter.validation';
import get from 'lodash/fp/get';
import isEqual from 'lodash/isEqual';
import keys from 'lodash/keys';
import styled from '@emotion/styled';
import { theme } from '../../../../shared/styles/core/theme';
import { tokens } from '../../../../shared/styles/core/tokens';
import { useAutoCompleteSections } from '../../hooks/useAutocomplete';
import { useDropDownDataState } from '../../state/dropdown-data.state';
import { useTableDataState } from "../../state/table-data.state";
import { yupResolver } from '@hookform/resolvers/yup';

const BOOLEAN_FILTER_FIELDS = [
   'locked',
   'unlocked',
   'dwpDeviates',
   'isBase',
   'excludeConnected',
   'missingGradeId',
   'outgoingGradeIds'
] as const;

const EXCLUDED_DISPLAY_FIELDS = ['inactivatedRowsHidden', 'search'] as const;
const AUTOCOMPLETE_FIELDS = ['buCodePru', 'buCodePdu', 'part', 'partDetail', 'itemState'] as const;

const isBooleanField = (field: string): field is typeof BOOLEAN_FILTER_FIELDS[number] =>
   BOOLEAN_FILTER_FIELDS.includes(field as typeof BOOLEAN_FILTER_FIELDS[number]);

type TableQuery = {
   pageSize?: number;
   offset?: number;
   progress?: any;
   inactivatedRowsHidden?: boolean;
   [key: string]: any;
};

type FilterSheetState = {
   isOpen: boolean;
   autoCompleteOpen: boolean;
   focusOnOpen: boolean;
   isSaveDisabled: boolean;
   lockedStates: LockedStates;
};

type FilterSheetAction =
   | { type: 'OPEN_MODAL' }
   | { type: 'CLOSE_MODAL' }
   | { type: 'SET_AUTOCOMPLETE_OPEN', payload: boolean }
   | { type: 'SET_SAVE_DISABLED', payload: boolean }
   | { type: 'SET_FOCUS_ON_OPEN', payload: boolean }
   | { type: 'UPDATE_LOCKED_STATES', payload: Partial<LockedStates> }
   | { type: 'RESET_LOCKED_STATES' };

function filterSheetReducer(state: FilterSheetState, action: FilterSheetAction): FilterSheetState {
   switch (action.type) {
      case 'OPEN_MODAL':
         return { ...state, isOpen: true, focusOnOpen: true };
      case 'CLOSE_MODAL':
         return { ...state, isOpen: false };
      case 'SET_AUTOCOMPLETE_OPEN':
         return { ...state, autoCompleteOpen: action.payload };
      case 'SET_SAVE_DISABLED':
         return { ...state, isSaveDisabled: action.payload };
      case 'SET_FOCUS_ON_OPEN':
         return { ...state, focusOnOpen: action.payload };
      case 'UPDATE_LOCKED_STATES':
         return {
            ...state,
            lockedStates: { ...state.lockedStates, ...action.payload }
         };
      case 'RESET_LOCKED_STATES':
         return {
            ...state,
            lockedStates: {
               locked: false,
               unlocked: false,
               dwpDeviates: false,
               isBase: false,
               excludeConnected: false,
               missingGradeId: false,
               outgoingGradeIds: false
            }
         };
      default:
         return state;
   }
}

const INITIAL_FILTER_STATE: FilterSheetState = {
   isOpen: false,
   autoCompleteOpen: false,
   focusOnOpen: true,
   isSaveDisabled: true,
   lockedStates: {
      locked: false,
      unlocked: false,
      dwpDeviates: false,
      isBase: false,
      excludeConnected: false,
      missingGradeId: false,
      outgoingGradeIds: false,
   }
};

const getQuerySnapshot = (): TableQuery => {
   try {
      return useTableDataState.getState().query || {};
   } catch (error) {
      console.error('Error getting query snapshot:', error);
      return {};
   }
};

const shouldShowClearButton = (key: string, value: unknown): boolean => {
   if (key === 'itemNos') {
      return Array.isArray(value) && value.length > 0;
   }
   return typeof value === 'string' && value.length > 0;
};

type TextFieldRowProps = {
   fieldKey: string;
   index: number;
   control: Control<FilterValues>;
   firstTextFieldRef?: (node: HTMLElement | null) => void;
   pendingFilter: Partial<FilterValues>;
   onClearField: (field: keyof FilterValues) => void;
   setValue: UseFormSetValue<FilterValues>;
   onClearItemNosValidation: () => void;
   helperText?: string;
   onChg?: (e: React.ChangeEvent<HTMLInputElement>) => void;
   onBlur?: () => void;
   onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

const TextFieldRow = ({
   fieldKey,
   index,
   control,
   firstTextFieldRef,
   pendingFilter,
   onClearField,
   setValue,
   onClearItemNosValidation,
   helperText,
   onChg,
   onBlur,
   onKeyDown,
}: TextFieldRowProps) => (
   <StyledSheetDiv>
      <div style={{ position: 'relative' }}>
         <CustomTextField
            control={control}
            field={fieldKey as keyof FilterValues}
            label={get(fieldKey, FILTERABLE_FIELDS)}
            disabled={false}
            firstTextFieldRef={fieldKey === 'itemName' || (index === 0 && fieldKey !== 'itemNos') ? firstTextFieldRef : undefined}
            index={index}
            helperText={helperText}
            onChg={onChg}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
         />
         {shouldShowClearButton(fieldKey, pendingFilter[fieldKey as keyof FilterValues]) && (
            <ClearButton
               onClick={() => {
                  if (fieldKey === 'itemNos') {
                     setValue('itemNos', []);
                     onClearItemNosValidation();
                  } else {
                     onClearField(fieldKey as keyof FilterValues);
                  }
               }}
               type="button"
            >
               ✕
            </ClearButton>
         )}
      </div>
   </StyledSheetDiv>
);

type AutoCompleteFieldRowProps = {
   fieldKey: string;
   index: number;
   control: Control<FilterValues>;
   getAutoCompleteOptions: (key: string) => string[];
   getAutoCompleteSections: (key: string) => AutoCompleteSection[];
   firstTextFieldRef?: (node: HTMLElement | null) => void;
   setValue: UseFormSetValue<FilterValues>;
   onOpenStateChange: (isOpen: boolean) => void;
};

const AutoCompleteFieldRow = ({
   fieldKey,
   index,
   control,
   getAutoCompleteOptions,
   getAutoCompleteSections,
   firstTextFieldRef,
   setValue,
   onOpenStateChange,
}: AutoCompleteFieldRowProps) => (
   <StyledSheetDiv>
      <CustomTextFieldAutoCompl
         control={control}
         field={fieldKey}
         label={get(fieldKey, FILTERABLE_FIELDS)}
         disabled={false}
         autoCompleteOptions={getAutoCompleteOptions(fieldKey)}
         autoCompleteSections={getAutoCompleteSections(fieldKey)}
         firstTextFieldRef={firstTextFieldRef}
         index={index}
         multiple={isArrayField(fieldKey)}
         freeSolo={false}
         openState={onOpenStateChange}
         noOptionsText='No matches'
         filterOptions={prefixFirstFilter}
         onChange={(value: string | string[] | null) => {
            if (isArrayField(fieldKey)) {
               setValue(fieldKey as keyof FilterValues,
                  Array.isArray(value) ? value : value ? [value] : []);
            } else {
               setValue(fieldKey as keyof FilterValues, value as string);
            }
         }}
      />
   </StyledSheetDiv>
);

type LockStateFieldProps = {
   fieldKey: keyof FilterValues;
   control: Control<FilterValues>;
   lockedStates: LockedStates;
   setValue: UseFormSetValue<FilterValues>;
   dispatch: React.Dispatch<FilterSheetAction>;
};

const LockStateField = ({
   fieldKey,
   control,
   lockedStates,
   setValue,
   dispatch,
}: LockStateFieldProps) => (
   <Controller
      key={fieldKey}
      control={control}
      name={fieldKey}
      render={({ field: { onChange, onBlur } }) => (
         <StyledSheetDiv style={{ justifyContent: 'space-between', display: 'flex' }}>
            {(FILTERABLE_FIELDS as Record<string, string>)[fieldKey]}
            <StyledIndeterminateCheckBox
               onBlur={onBlur}
               onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  try {
                     const newStates = { ...lockedStates };
                     newStates[fieldKey as keyof LockedStates] = e.target.checked;

                     if (fieldKey === "locked" && e.target.checked) {
                        newStates.unlocked = false;
                     }
                     if (fieldKey === "unlocked" && e.target.checked) {
                        newStates.locked = false;
                     }
                     if (fieldKey === "missingGradeId" && e.target.checked) {
                        newStates.outgoingGradeIds = false;
                     }
                     if (fieldKey === "outgoingGradeIds" && e.target.checked) {
                        newStates.missingGradeId = false;
                     }

                     dispatch({ type: 'UPDATE_LOCKED_STATES', payload: newStates });

                     BOOLEAN_FILTER_FIELDS.forEach(field => {
                        setValue(field, newStates[field as keyof LockedStates]);
                     });

                     dispatch({ type: 'SET_SAVE_DISABLED', payload: false });

                     onChange(e);
                  } catch (error) {
                     console.error(`Error handling lock state change for ${String(fieldKey)}:`, error);
                  }
               }}
               id={fieldKey}
               value={fieldKey}
               checked={lockedStates[fieldKey as keyof LockedStates]}
            />
         </StyledSheetDiv>
      )}
   />
);

export const FilterSheet = () => {
   const [state, dispatch] = useReducer(filterSheetReducer, INITIAL_FILTER_STATE);
   const { isOpen, autoCompleteOpen, focusOnOpen, isSaveDisabled, lockedStates } = state;

   const [itemNosValidation, setItemNosValidation] = useState<{
      invalid: string[];
      duplicatesRemoved: number;
   } | null>(null);

   const prevIsSaveDisabled = useRef(isSaveDisabled);
   const forceResetKey = useRef(0);
   const prevQueryRef = useRef<Record<string, any> | null>(null);

   const dropDownData = useDropDownDataState((state) => state.data);
   const [partDetails, materialParts, itemStates] = useDropDownDataState((state) => [
      state.data.materialTypes,
      state.data.materialParts,
      state.data.itemStates
   ]);

   const { isFiltered, reset } = useFiltering();

   const {
      control,
      handleSubmit,
      watch,
      setValue,
      reset: resetForm,
      formState: { isValid },
   } = useForm<FilterValues>({
      mode: 'all',
      defaultValues: filterValidationSchema.cast({}) as FilterValues,
      resolver: yupResolver(filterValidationSchema),
   });

   const pendingFilter = watch();

   const extractMeaningfulValues = useCallback((source: Record<string, any>): Record<string, any> => {
      const result: Record<string, any> = {};

      Object.keys(FILTERABLE_FIELDS).forEach(key => {
         if (key === 'inactivatedRowsHidden') return;

         let value = source[key];

         if (key === 'itemNos') {
            if (typeof value === 'string') {
               const parts = value.split(',').map(p => p.trim()).filter(p => p);
               value = parts.length > 0 ? parts : [];
            }
         }

         if (isBooleanField(key)) {
            if (value === true) {
               result[key] = true;
            }
            return;
         }

         if (isArrayField(key)) {
            if (Array.isArray(value) && value.length > 0) {
               result[key] = [...value];
            }
            return;
         }

         if (typeof value === 'string') {
            if (value.trim().length > 0) {
               result[key] = value.trim();
            }
            return;
         }

         if (value !== null && value !== undefined) {
            result[key] = value;
         }
      });

      return result;
   }, []);

   const initializeFilterForm = useCallback((queryData: Record<string, any>, shouldResetKey = true) => {
      try {
         const emptyDefaults = filterValidationSchema.cast({}) as FilterValues;
         resetForm(emptyDefaults);

         dispatch({ type: 'RESET_LOCKED_STATES' });
         setItemNosValidation(null);

         requestAnimationFrame(() => {
            keys(FILTERABLE_FIELDS).forEach((field) => {
               if (field === 'inactivatedRowsHidden') return;

               const queryValue = get(field)(queryData);

               if (queryValue !== undefined) {
                  setValue(field as keyof FilterValues, queryValue, {
                     shouldValidate: false,
                     shouldDirty: false
                  });

                  if (isBooleanField(field)) {
                     dispatch({
                        type: 'UPDATE_LOCKED_STATES',
                        payload: { [field as keyof LockedStates]: queryValue }
                     });
                  }
               }
            });

            const newLockedStates = {} as LockedStates;
            BOOLEAN_FILTER_FIELDS.forEach(field => {
               newLockedStates[field as keyof LockedStates] = get(field)(queryData) || false;
            });

            dispatch({ type: 'UPDATE_LOCKED_STATES', payload: newLockedStates });

            if (shouldResetKey) {
               forceResetKey.current += 1;
            }
         });
      } catch (error) {
         console.error('Error initializing filter form:', error);
      }
   }, [resetForm, setValue]);

   const handleClearField = useCallback((field: keyof FilterValues) => {
      try {
         if (isArrayField(field as string)) {
            setValue(field, []);
         } else if (isBooleanField(field as string)) {
            setValue(field, false);
            dispatch({
               type: 'UPDATE_LOCKED_STATES',
               payload: { [field as keyof LockedStates]: false }
            });
         } else {
            setValue(field, "");
         }

         if (field === 'itemNos') {
            setItemNosValidation(null);
         }

         dispatch({ type: 'SET_SAVE_DISABLED', payload: false });
      } catch (error) {
         console.error(`Error clearing field ${String(field)}:`, error);
      }
   }, [setValue]);

   useEffect(() => {
      if (isOpen) {
         try {
            initializeFilterForm(getQuerySnapshot());
         } catch (error) {
            console.error('Error handling modal open:', error);
         }
      }
   }, [isOpen, initializeFilterForm]);

   useEffect(() => {
      const unsubscribe = useTableDataState.subscribe((state) => {
         if (!isOpen) return;

         try {
            const newQuery = state.query;

            if (isEqual(prevQueryRef.current, newQuery)) return;
            forceResetKey.current += 1;

            prevQueryRef.current = { ...newQuery };
            initializeFilterForm(newQuery, false);
         } catch (error) {
            console.error('Error in table data subscription:', error);
         }
      });

      return () => unsubscribe();
   }, [isOpen, initializeFilterForm]);

   const shouldDisableSave = useMemo(() => {
      if (!isOpen) return true;

      try {
         const currentValues = extractMeaningfulValues(getQuerySnapshot());
         const pendingValues = extractMeaningfulValues(pendingFilter);
         const hasChanged = !isEqual(currentValues, pendingValues);
         return !isValid || !hasChanged;
      } catch (error) {
         console.error('Error calculating save button state:', error);
         return true;
      }
   }, [pendingFilter, isValid, isOpen, extractMeaningfulValues]);

   useEffect(() => {
      if (prevIsSaveDisabled.current !== shouldDisableSave) {
         dispatch({ type: 'SET_SAVE_DISABLED', payload: shouldDisableSave });
         prevIsSaveDisabled.current = shouldDisableSave;
      }
   }, [shouldDisableSave]);

   const {
      getAutoCompleteSections,
      getAutoCompleteOptions
   } = useAutoCompleteSections({
      dropDownData,
      materialParts,
      partDetails,
      itemStates,
      parts: [],
      currentPart: control._formValues?.part,
      currentPartDetail: control._formValues?.partDetail,
      getPartDetailOptions: () => partDetails || [],
      getFilteredParts: () => materialParts || [],
      getCompatiblePartDetails: () => [],
      getMaterialGradeOptions: () => [],
      getPartOptions: () => materialParts || []
   });

   const firstTextFieldRef = useCallback(
      (node: HTMLElement | null) => {
         if (node !== null && isOpen && focusOnOpen) {
            node.focus();
            dispatch({ type: 'SET_FOCUS_ON_OPEN', payload: false });
         }
      },
      [isOpen, focusOnOpen]
   );

   const handleSave = useCallback((newFilter: Record<string, any>) => {
      try {
         if (Array.isArray(newFilter.itemNos)) {
            const cleanedItems = newFilter.itemNos
               .filter(item => item && item.trim() !== '')
               .join(',');

            if (cleanedItems) {
               newFilter.itemNos = formatItemNumbers(cleanedItems);
            } else {
               newFilter.itemNos = [];
            }
         }

         const currentQuery = getQuerySnapshot();
         const updatedQuery: Record<string, any> = { ...currentQuery };

         Object.keys(FILTERABLE_FIELDS).forEach(key => {
            if (key === 'inactivatedRowsHidden') return;

            const value = newFilter[key];

            if (isBooleanField(key)) {
               if (value === true) {
                  updatedQuery[key] = true;
               } else {
                  delete updatedQuery[key];
               }
               return;
            }

            const isEmpty =
               value === undefined ||
               value === null ||
               value === '' ||
               (Array.isArray(value) && value.length === 0) ||
               (typeof value === 'string' && value.trim() === '');

            if (isEmpty) {
               delete updatedQuery[key];
            } else if (isArrayField(key) && !Array.isArray(value)) {
               updatedQuery[key] = [value];
            } else {
               updatedQuery[key] = value;
            }
         });

         updatedQuery.pageSize = currentQuery.pageSize || 25;
         updatedQuery.offset = 0;
         if (currentQuery.progress) updatedQuery.progress = currentQuery.progress;
         if (currentQuery.inactivatedRowsHidden !== undefined) {
            updatedQuery.inactivatedRowsHidden = currentQuery.inactivatedRowsHidden;
         }

         useTableDataState.setState({ query: updatedQuery as any });
         dispatch({ type: 'CLOSE_MODAL' });
         setItemNosValidation(null);
      } catch (error) {
         console.error('Error saving filter:', error);
      }
   }, []);

   const handleReset = useCallback(() => {
      try {
         dispatch({ type: 'RESET_LOCKED_STATES' });
         setItemNosValidation(null);

         const currentInactiveHidden = getQuerySnapshot().inactivatedRowsHidden;
         reset();

         if (currentInactiveHidden !== undefined) {
            requestAnimationFrame(() => {
               const resetQuery = useTableDataState.getState().query;
               useTableDataState.setState({
                  query: { ...resetQuery, inactivatedRowsHidden: currentInactiveHidden }
               });
            });
         }

         BOOLEAN_FILTER_FIELDS.forEach(field => {
            setValue(field, false);
         });

         Object.keys(FILTERABLE_FIELDS).forEach((field) => {
            if (field === 'inactivatedRowsHidden' || isBooleanField(field)) {
               return;
            }

            if (isArrayField(field)) {
               setValue(field as keyof FilterValues, []);
            } else {
               setValue(field as keyof FilterValues, "");
            }
         });

         dispatch({ type: 'CLOSE_MODAL' });
      } catch (error) {
         console.error('Error resetting filter:', error);
      }
   }, [reset, setValue]);

   const handleItemNosChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setValue("itemNos", value as any);
      setItemNosValidation(null);
   }, [setValue]);

   const handleItemNosBlur = useCallback(() => {
      const currentValue = control._formValues.itemNos;

      if (!currentValue || (Array.isArray(currentValue) && currentValue.length === 0)) {
         setItemNosValidation(null);
         return;
      }

      const valueString = Array.isArray(currentValue)
         ? currentValue.join(',')
         : String(currentValue);

      if (!valueString.trim()) {
         setValue("itemNos", []);
         setItemNosValidation(null);
         return;
      }

      const result = validateItemNumberInput(valueString);

      if (result.valid.length > 0) {
         setValue("itemNos", result.valid);
         setItemNosValidation({
            invalid: result.invalid,
            duplicatesRemoved: result.duplicatesRemoved
         });
      } else if (result.invalid.length > 0) {
         setItemNosValidation({
            invalid: result.invalid,
            duplicatesRemoved: 0
         });
      }
   }, [setValue, control._formValues]);

   const handleItemNosKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
         e.preventDefault();
         const target = e.target as HTMLInputElement;
         target.blur();

         setTimeout(() => {
            if (!isSaveDisabled) {
               handleSubmit(handleSave)();
            }
         }, 0);
      }
   }, [isSaveDisabled, handleSubmit, handleSave]);

   const handleCloseModal = useCallback(() => {
      dispatch({ type: 'CLOSE_MODAL' });
      setItemNosValidation(null);
   }, []);

   const handleOpenStateChange = useCallback((open: boolean) => {
      dispatch({ type: 'SET_AUTOCOMPLETE_OPEN', payload: open });
   }, []);

   const clearItemNosValidation = useCallback(() => {
      setItemNosValidation(null);
   }, []);

   useEscapeKey(handleCloseModal, isOpen);

   const keyDownHandler = useEnterKeySubmit(
      () => handleSubmit(handleSave)(),
      autoCompleteOpen
   );

   const getItemNosHelperText = useCallback(() => {
      const baseText = "Enter 8-digit item numbers separated by comma or space.";

      if (!itemNosValidation) {
         return baseText;
      }

      const validationMessages: string[] = [];

      if (itemNosValidation.invalid.length > 0) {
         const shown = itemNosValidation.invalid.slice(0, 3).join(', ');
         const more = itemNosValidation.invalid.length > 3 ? '...' : '';
         validationMessages.push(`Skipped ${itemNosValidation.invalid.length} invalid: ${shown}${more}`);
      }

      if (itemNosValidation.duplicatesRemoved > 0) {
         validationMessages.push(`Removed ${itemNosValidation.duplicatesRemoved} duplicate(s)`);
      }

      if (validationMessages.length > 0) {
         return `${baseText}\n${validationMessages.join('\n')}`;
      }

      return baseText;
   }, [itemNosValidation]);

   return (
      <>
         <StyledTooltip title={'Filter results'}>
            <StyledToolbarButton
               onClick={() => dispatch({ type: 'OPEN_MODAL' })}
               ssrIcon={filterIcon}
               text="Filters"
               iconPosition={'trailing'}
               small={true}
            />
         </StyledTooltip>
         <Modal
            visible={isOpen}
            handleCloseBtn={handleCloseModal}
            onKeyDown={keyDownHandler as any}
            escapable={false}
         >
            <StyledSheets
               alignment={'right'}
               preserveAlignment={true}
               size={'small'}
               header={<ModalHeader title="Filters" floating={false} />}
               footer={
                  <ModalFooter compact={false}>
                     <Button
                        text="Save"
                        type="primary"
                        disabled={isSaveDisabled}
                        onClick={handleSubmit(handleSave)}
                     />
                     <Button
                        text="Reset filters"
                        type="secondary"
                        disabled={!isFiltered}
                        onClick={handleReset}
                     />
                  </ModalFooter>
               }
            >
               <ModalBody>
                  <div>
                     {keys(FILTERABLE_FIELDS).map((key, index) => {
                        if ((EXCLUDED_DISPLAY_FIELDS as readonly string[]).includes(key)) return null;

                        if (key === 'itemNos') {
                           return (
                              <TextFieldRow
                                 key={key}
                                 fieldKey={key}
                                 index={index}
                                 control={control}
                                 firstTextFieldRef={firstTextFieldRef}
                                 pendingFilter={pendingFilter}
                                 onClearField={handleClearField}
                                 setValue={setValue}
                                 onClearItemNosValidation={clearItemNosValidation}
                                 helperText={getItemNosHelperText()}
                                 onChg={handleItemNosChange}
                                 onBlur={handleItemNosBlur}
                                 onKeyDown={handleItemNosKeyDown}
                              />
                           );
                        }

                        if (key === 'itemName') {
                           return (
                              <TextFieldRow
                                 key={key}
                                 fieldKey={key}
                                 index={index}
                                 control={control}
                                 firstTextFieldRef={firstTextFieldRef}
                                 pendingFilter={pendingFilter}
                                 onClearField={handleClearField}
                                 setValue={setValue}
                                 onClearItemNosValidation={clearItemNosValidation}
                              />
                           );
                        }

                        if (isBooleanField(key)) {
                           return (
                              <LockStateField
                                 key={key}
                                 fieldKey={key as keyof FilterValues}
                                 control={control}
                                 lockedStates={lockedStates}
                                 setValue={setValue}
                                 dispatch={dispatch}
                              />
                           );
                        }

                        if (isArrayField(key) || (AUTOCOMPLETE_FIELDS as readonly string[]).includes(key)) {
                           return (
                              <AutoCompleteFieldRow
                                 key={key}
                                 fieldKey={key}
                                 index={index}
                                 control={control}
                                 getAutoCompleteOptions={getAutoCompleteOptions}
                                 getAutoCompleteSections={getAutoCompleteSections}
                                 firstTextFieldRef={firstTextFieldRef}
                                 setValue={setValue}
                                 onOpenStateChange={handleOpenStateChange}
                              />
                           );
                        }

                        return (
                           <TextFieldRow
                              key={key}
                              fieldKey={key}
                              index={index}
                              control={control}
                              firstTextFieldRef={firstTextFieldRef}
                              pendingFilter={pendingFilter}
                              onClearField={handleClearField}
                              setValue={setValue}
                              onClearItemNosValidation={clearItemNosValidation}
                           />
                        );
                     })}
                  </div>
               </ModalBody>
            </StyledSheets>
         </Modal>
      </>
   );
};

const ClearButton = styled.button`
   position: absolute;
   right: ${tokens.space.sm};
   top: ${tokens.space.lg};
   background: none;
   border: none;
   cursor: pointer;
   z-index: ${tokens.zIndex.base};
   color: ${theme.colors.text.disabled};

   &:hover {
      color: ${theme.colors.text.primary};
   }
`;